import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trash2, Phone, Users, Mail, MessageSquare, Presentation, MapPin, FileUp, HandHeart, Cog, RotateCcw, MessageCircle } from 'lucide-react';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { listarUsuarios } from '../../usuarios/api';
import { useInteracoes, useRemoverInteracao } from '../hooks';
import type { OpportunityInteractionTipo, OpportunityInteraction } from '../api';
import { formatDateTimeBR } from '../utils/format';

const TIPO_META: Record<
  OpportunityInteractionTipo,
  { label: string; Icon: React.ComponentType<{ className?: string }> }
> = {
  ligacao: { label: 'Ligação', Icon: Phone },
  reuniao: { label: 'Reunião', Icon: Users },
  email: { label: 'E-mail', Icon: Mail },
  whatsapp: { label: 'WhatsApp', Icon: MessageSquare },
  apresentacao: { label: 'Apresentação', Icon: Presentation },
  visita: { label: 'Visita', Icon: MapPin },
  envio_material: { label: 'Envio de material', Icon: FileUp },
  negociacao: { label: 'Negociação', Icon: HandHeart },
  alinhamento_tecnico: { label: 'Alinhamento técnico', Icon: Cog },
  follow_up: { label: 'Follow-up', Icon: RotateCcw },
  outro: { label: 'Outro', Icon: MessageCircle },
};

interface Props {
  opportunityId: string;
}

/**
 * Lista as interações registradas na oportunidade. Cada linha mostra
 * o tipo (com ícone), quando aconteceu, autor e resumo; ao clicar em
 * "remover" abre `ConfirmDialog` — só o autor ou perfil privilegiado
 * conseguem remover (o backend enforça, o frontend só tenta).
 */
export const OpportunityInteractionsList: React.FC<Props> = ({ opportunityId }) => {
  const { data, isLoading, isError, error } = useInteracoes(opportunityId);
  const [pendingDelete, setPendingDelete] = useState<OpportunityInteraction | null>(
    null,
  );
  const [deleteErr, setDeleteErr] = useState<string | null>(null);
  const remover = useRemoverInteracao(opportunityId);

  const { data: usuariosResp } = useQuery({
    queryKey: ['picker', 'usuarios'],
    queryFn: () => listarUsuarios({ limit: 200 }),
    staleTime: 60 * 1000,
  });
  const userNameById = useMemo(() => {
    const m = new Map<string, string>();
    (usuariosResp?.data ?? []).forEach((u) => m.set(u.id, u.name));
    return m;
  }, [usuariosResp]);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <Alert variant="error">
        Erro ao carregar interações: {(error as Error)?.message ?? 'Falha desconhecida.'}
      </Alert>
    );
  }
  if (!data || data.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Nenhuma interação registrada. Registre a primeira para iniciar a timeline
        comercial.
      </p>
    );
  }

  return (
    <>
      <ul className="space-y-2">
        {data.map((it) => {
          const meta = TIPO_META[it.tipo];
          const Icon = meta.Icon;
          return (
            <li
              key={it.id}
              className="flex items-start gap-3 rounded-md border border-gray-200 bg-white p-3 shadow-sm"
            >
              <span
                className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-blue-100 text-blue-700"
                aria-hidden
              >
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-medium text-gray-900">{meta.label}</span>
                  <span className="text-xs text-gray-500">
                    {formatDateTimeBR(it.occurredAt)}
                  </span>
                </div>
                <p className="mt-0.5 whitespace-pre-wrap text-sm text-gray-700">
                  {it.summary}
                </p>
                {it.createdBy && (
                  <p className="mt-1 text-[10px] text-gray-400">
                    Registrado por{' '}
                    <span className="font-medium text-gray-500">
                      {userNameById.get(it.createdBy) ?? 'usuário desconhecido'}
                    </span>
                    {' '}em {formatDateTimeBR(it.createdAt)}
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={() => {
                  setDeleteErr(null);
                  setPendingDelete(it);
                }}
                className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                title="Remover"
                aria-label="Remover interação"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          );
        })}
      </ul>

      {pendingDelete && (
        <>
          <ConfirmDialog
            open
            title="Remover interação"
            message={`Tem certeza que deseja remover a interação: "${pendingDelete.summary.slice(0, 100)}${pendingDelete.summary.length > 100 ? '…' : ''}"?`}
            confirmLabel="Remover"
            danger
            busy={remover.isPending}
            onConfirm={async () => {
              try {
                await remover.mutateAsync(pendingDelete.id);
                setPendingDelete(null);
              } catch (e) {
                setDeleteErr(getFriendlyErrorMessage(e, 'Falha ao remover interação.'));
              }
            }}
            onCancel={() => setPendingDelete(null)}
          />
          {deleteErr && (
            <div className="fixed bottom-4 right-4 z-[60] max-w-md">
              <Alert variant="error">{deleteErr}</Alert>
            </div>
          )}
        </>
      )}
    </>
  );
};
